import { useMemo, useState, useEffect } from 'react'
import useDealStore from '../store/dealStore'
import '../styles/module9.css'

const money = (value) => `$${Math.round(Number(value) || 0).toLocaleString()}`

const PRIORITY_LABELS = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

const CATEGORY_LABELS = {
  broker: 'Broker',
  offer: 'Offer',
  lender: 'Lender',
  meeting: 'Meeting',
  diligence: 'Diligence',
  closing: 'Closing',
}

const PRIORITY_WEIGHT = { urgent: 95, high: 80, medium: 58, low: 35 }

function addDays(days) {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return date.toISOString()
}

function formatDueDate(dateString) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(new Date(dateString))
}

function getOfferPrice(scoreAndOffer) {
  const offer = scoreAndOffer?.offerPrice
  if (typeof offer === 'number') return offer
  return offer?.recommended || offer?.maximum || offer?.minimum || 0
}

function buildActionPlan(dealState) {
  const raw = dealState.rawDealData || {}
  const score = dealState.scoreAndOffer || {}
  const lenderMatches = dealState.lenderMatches || {}
  const topLender = lenderMatches.topRecommendation || {}
  const loanPackage = dealState.loanPackage || {}
  const outreach = dealState.outreachMaterials || {}
  const dealScore = Number(score.numericalScore) || Number(raw.initialAIScore) || 0
  const offerPrice = getOfferPrice(score)
  const hasLoanPackage = Boolean(loanPackage.documentId)
  const hasLenderTemplates = Boolean(outreach.emailTemplates?.length)
  const hasTopLender = Boolean(topLender.lenderName)
  const brokerName = raw.dealLeadGenerationDetails?.brokerName || 'Listing Broker'

  const tasks = [
    {
      id: 'call-broker',
      title: 'Call Listing Broker',
      description: `Confirm seller expectations, process timing, available diligence files, and whether there are competing offers.`,
      category: 'broker',
      priority: 'urgent',
      dueDate: addDays(1),
      status: hasLenderTemplates ? 'ready' : 'pending',
      owner: 'Acquisitions',
      source: brokerName,
      impact: 'Protects momentum before LOI submission.',
    },
    {
      id: 'send-loi',
      title: 'Send LOI',
      description: `Prepare offer terms using the recommended offer price of ${offerPrice ? money(offerPrice) : 'TBD'} and the deal verdict from Module 5.`,
      category: 'offer',
      priority: dealScore >= 75 ? 'urgent' : 'high',
      dueDate: addDays(2),
      status: 'pending',
      owner: 'Principal',
      source: 'Deal Score & Verdict',
      impact: 'Moves the deal from evaluation into active negotiation.',
    },
    {
      id: 'follow-up-lenders',
      title: 'Follow Up with Lenders',
      description: hasTopLender
        ? `Follow up with ${topLender.lenderName} and the other matched lenders on preliminary terms, DSCR comfort, and next document requests.`
        : 'Follow up with matched lenders on preliminary terms, DSCR comfort, and next document requests.',
      category: 'lender',
      priority: hasLoanPackage ? 'high' : 'medium',
      dueDate: addDays(3),
      status: hasLoanPackage ? 'ready' : 'blocked',
      owner: 'Capital Markets',
      source: hasLoanPackage ? 'Loan Package Generated' : 'Waiting on loan package',
      impact: 'Keeps debt options warm while the offer is being prepared.',
    },
    {
      id: 'book-meeting',
      title: 'Book Meeting',
      description: 'Schedule a working call with the broker, seller, or internal investment committee to review terms and timing.',
      category: 'meeting',
      priority: 'medium',
      dueDate: addDays(4),
      status: 'pending',
      owner: 'Acquisitions',
      source: 'Outreach & Communication',
      impact: 'Creates a concrete next conversation instead of passive waiting.',
    },
    {
      id: 'request-seller-call',
      title: 'Request Seller Call',
      description: 'Ask for direct seller access to clarify motivation, flexibility on credits, and preferred closing certainty.',
      category: 'broker',
      priority: 'medium',
      dueDate: addDays(5),
      status: 'pending',
      owner: 'Principal',
      source: 'Broker / Seller Outreach',
      impact: 'Improves negotiation context before finalizing terms.',
    },
    {
      id: 'order-appraisal',
      title: 'Order Appraisal',
      description: 'Prepare appraisal scope and vendor shortlist so the request can launch immediately after offer acceptance.',
      category: 'diligence',
      priority: 'low',
      dueDate: addDays(7),
      status: 'pending',
      owner: 'Transaction Team',
      source: 'Closing Readiness',
      impact: 'Reduces friction once the deal moves under contract.',
    },
    {
      id: 'start-due-diligence',
      title: 'Start Due Diligence',
      description: 'Create the first diligence checklist covering rent roll audit, T12 review, inspections, title, insurance, and third-party reports.',
      category: 'diligence',
      priority: 'high',
      dueDate: addDays(7),
      status: 'pending',
      owner: 'Transaction Team',
      source: 'Module 14 Prep',
      impact: 'Pre-builds the diligence motion before the PSA clock starts.',
    },
  ]

  const completedCount = tasks.filter((task) => task.status === 'complete').length
  const readyCount = tasks.filter((task) => task.status === 'ready').length
  const blockedCount = tasks.filter((task) => task.status === 'blocked').length
  const momentumScore = Math.min(98, Math.max(35, Math.round(dealScore * 0.55 + readyCount * 9 - blockedCount * 7 + (hasLenderTemplates ? 12 : 0))))

  return {
    tasks,
    momentumScore,
    completedCount,
    readyCount,
    blockedCount,
    timeline: {
      immediate: tasks.filter((task) => ['urgent', 'high'].includes(task.priority)).length,
      thisWeek: tasks.length,
      nextMilestone: 'LOI Generator',
    },
  }
}

function statusLabel(status) {
  return {
    ready: 'Ready',
    pending: 'Pending',
    blocked: 'Blocked',
    complete: 'Done',
  }[status] || 'Pending'
}

function scoreTask(task, dealState) {
  const dealScore = Number(dealState.scoreAndOffer?.numericalScore) || Number(dealState.rawDealData?.initialAIScore) || 0
  const statusBonus = task.status === 'ready' ? 10 : task.status === 'blocked' ? -18 : task.status === 'complete' ? -35 : 0
  const dueDays = Math.max(0, Math.ceil((new Date(task.dueDate) - new Date()) / 86400000))
  const dueBonus = dueDays <= 1 ? 12 : dueDays <= 3 ? 8 : dueDays <= 7 ? 4 : 0
  const lenderBonus = task.category === 'lender' && dealState.loanPackage?.documentId ? 8 : 0
  const score = (PRIORITY_WEIGHT[task.priority] || 45) + statusBonus + dueBonus + lenderBonus + Math.round(dealScore / 20)
  return Math.max(0, Math.min(100, score))
}

function buildProactivePrompts(dealState, tasks) {
  const ids = new Set(tasks.map((task) => task.id))
  const prompts = []
  const topLender = dealState.lenderMatches?.topRecommendation?.lenderName

  if (dealState.outreachMaterials?.followUpSequenceCreated && !ids.has('review-module8-replies')) {
    prompts.push({
      id: 'review-module8-replies',
      title: 'Review Outreach Replies',
      description: 'Check responses from Module 8 and convert any lender, broker, or seller replies into next actions.',
      category: 'broker',
      priority: 'high',
      dueDate: addDays(1),
      status: 'ready',
      owner: 'Acquisitions',
      source: 'Module 9 Outreach',
      impact: 'Turns communication activity into a concrete workflow instead of letting replies sit idle.',
    })
  }

  if (dealState.loanPackage?.documentId && !ids.has('confirm-lender-package')) {
    prompts.push({
      id: 'confirm-lender-package',
      title: topLender ? `Confirm ${topLender} Package Review` : 'Confirm Lender Package Review',
      description: 'Verify lender receipt of the loan package, ask for preliminary constraints, and confirm the next underwriting item needed.',
      category: 'lender',
      priority: 'urgent',
      dueDate: addDays(1),
      status: 'ready',
      owner: 'Capital Markets',
      source: 'Loan Package Sent',
      impact: 'Prevents the financing track from falling behind the LOI timeline.',
    })
  }

  if ((Number(dealState.scoreAndOffer?.numericalScore) || 0) >= 80 && !ids.has('tighten-loi-timeline')) {
    prompts.push({
      id: 'tighten-loi-timeline',
      title: 'Tighten LOI Timeline',
      description: 'Use the strong deal score to prepare a faster LOI timeline while preserving diligence and financing protections.',
      category: 'offer',
      priority: 'high',
      dueDate: addDays(2),
      status: 'pending',
      owner: 'Principal',
      source: 'Deal Score & Verdict',
      impact: 'A strong offer posture can improve seller confidence without weakening protections.',
    })
  }

  return prompts
}

function Module9Page() {
  const { dealState, updateDealState, updateAvailableModules, forceNavigateToModule } = useDealStore()
  const generatedPlan = useMemo(() => buildActionPlan(dealState), [dealState])
  // Restore saved task statuses from dealState so completions survive navigation
  const [tasks, setTasks] = useState(() => {
    const saved = dealState.actionPlan?.tasks
    if (saved?.length) return saved
    return generatedPlan.tasks
  })
  const [activeTaskId, setActiveTaskId] = useState(generatedPlan.tasks[0]?.id)

  // Auto-persist task changes immediately (no need to click Advance)
  useEffect(() => {
    updateDealState({
      actionPlan: {
        ...(dealState.actionPlan || {}),
        tasks,
        statusTracking: {
          totalTasks: tasks.length,
          completedTasks: tasks.filter(t => t.status === 'complete').length,
        },
      }
    })
  }, [tasks]) // eslint-disable-line
  const [priorityFilter, setPriorityFilter] = useState('all')
  const [customTask, setCustomTask] = useState('')
  const [showAll, setShowAll] = useState(false)

  const dealName = dealState.dealName || 'Active Multifamily Deal'
  const raw = dealState.rawDealData || {}
  const location = [raw.location?.city, raw.location?.state].filter(Boolean).join(', ') || raw.location?.address || 'Target market'
  const activeTask = tasks.find((task) => task.id === activeTaskId) || tasks[0]
  const scoredTasks = useMemo(
    () => tasks.map((task) => ({ ...task, priorityScore: scoreTask(task, dealState) })),
    [tasks, dealState]
  )
  const filteredTasks = scoredTasks
    .filter((task) => priorityFilter === 'all' || task.priority === priorityFilter)
    .sort((a, b) => b.priorityScore - a.priorityScore)
  const visibleTasks = showAll ? filteredTasks : filteredTasks.slice(0, 5)
  const proactivePrompts = useMemo(() => buildProactivePrompts(dealState, tasks), [dealState, tasks])
  const completeCount = tasks.filter((task) => task.status === 'complete').length
  const urgentOpen = tasks.filter((task) => task.priority === 'urgent' && task.status !== 'complete').length
  const blockedCount = tasks.filter((task) => task.status === 'blocked').length
  const progress = Math.round((completeCount / Math.max(tasks.length, 1)) * 100)
  const momentumScore = Math.min(100, generatedPlan.momentumScore + completeCount * 4 - blockedCount * 5)
  const activeTaskScore = activeTask ? scoreTask(activeTask, dealState) : 0

  const toggleTask = (taskId) => {
    setTasks((current) => current.map((task) => {
      if (task.id !== taskId || task.status === 'blocked') return task
      return {
        ...task,
        status: task.status === 'complete' ? 'pending' : 'complete',
      }
    }))
  }

  const addCustomTask = () => {
    const title = customTask.trim()
    if (!title) return
    const task = {
      id: `custom-${Date.now()}`,
      title,
      description: 'Custom action added by the user for this deal.',
      category: 'closing',
      priority: 'medium',
      dueDate: addDays(5),
      status: 'pending',
      owner: 'Acquisitions',
      source: 'User Added',
      impact: 'Keeps deal-specific work visible in the action plan.',
    }
    setTasks((current) => [task, ...current])
    setActiveTaskId(task.id)
    setCustomTask('')
  }

  const applyPrompt = (prompt) => {
    setTasks((current) => [prompt, ...current])
    setActiveTaskId(prompt.id)
  }

  const rebuildAiPlan = () => {
    setTasks(generatedPlan.tasks)
    setActiveTaskId(generatedPlan.tasks[0]?.id)
    setPriorityFilter('all')
  }

  const handleComplete = () => {
    const sortedTasks = [...tasks].sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
    updateDealState({
      actionPlan: {
        tasks: sortedTasks,
        priorities: sortedTasks.map((task) => ({
          id: task.id,
          title: task.title,
          priority: task.priority,
          status: task.status,
          dueDate: task.dueDate,
          priorityScore: scoreTask(task, dealState),
        })),
        timeline: {
          generatedAt: new Date().toISOString(),
          nextSevenDays: sortedTasks.filter((task) => new Date(task.dueDate) <= new Date(addDays(7))).map((task) => task.id),
          nextMilestone: 'LOI Generator',
        },
        statusTracking: {
          totalTasks: sortedTasks.length,
          completedTasks: sortedTasks.filter((task) => task.status === 'complete').length,
          urgentOpen,
          blockedCount,
          momentumScore,
          proactivePrompts: proactivePrompts.map((prompt) => prompt.id),
        },
      },
    })
    updateAvailableModules()
    setTimeout(() => forceNavigateToModule(11), 500)
  }

  return (
    <div className="m9-page">
      <div className="m9-header">
        <div className="m9-badge">Module 9</div>
        <div className="m9-header-text">
          <h1>Actions &amp; Follow-up</h1>
          <p>AI prompts the next best actions, priorities, owners, and follow-ups to keep the deal moving.</p>
        </div>
        <div className="m9-header-stats">
          <div className="m9-stat">
            <span>Momentum</span>
            <strong>{momentumScore}</strong>
          </div>
          <div className="m9-stat">
            <span>Open Urgent</span>
            <strong>{urgentOpen}</strong>
          </div>
        </div>
      </div>

      <div className="m9-grid">
        <section className="m9-deal-card">
          <div className="m9-title-row">
            <span>Deal Context</span>
            <strong>Live Inputs</strong>
          </div>
          <h2>{dealName}</h2>
          <p>{location}</p>
          <div className="m9-context-grid">
            <div>
              <span>Asking Price</span>
              <strong>{money(raw.price)}</strong>
            </div>
            <div>
              <span>Units</span>
              <strong>{raw.property?.units || 'TBD'}</strong>
            </div>
            <div>
              <span>Deal Score</span>
              <strong>{dealState.scoreAndOffer?.numericalScore || raw.initialAIScore || 0}/100</strong>
            </div>
            <div>
              <span>Top Lender</span>
              <strong>{dealState.lenderMatches?.topRecommendation?.lenderName || 'TBD'}</strong>
            </div>
          </div>
          <div className="m9-workflow-actions">
            <button onClick={rebuildAiPlan}>Rebuild AI Plan</button>
            <span>{proactivePrompts.length} proactive prompt{proactivePrompts.length === 1 ? '' : 's'} available</span>
          </div>
        </section>

        <section className="m9-task-card">
          <div className="m9-title-row">
            <span>Next Actions</span>
            <strong>AI Recommendations</strong>
          </div>
          <div className="m9-filter-row" aria-label="Priority filter">
            {['all', 'urgent', 'high', 'medium', 'low'].map((priority) => (
              <button
                key={priority}
                className={priorityFilter === priority ? 'active' : ''}
                onClick={() => setPriorityFilter(priority)}
              >
                {priority === 'all' ? 'All' : PRIORITY_LABELS[priority]}
              </button>
            ))}
          </div>
          <div className="m9-task-list">
            {visibleTasks.map((task) => (
              <button
                key={task.id}
                className={`m9-task-row ${task.status} ${activeTaskId === task.id ? 'active' : ''}`}
                onClick={() => setActiveTaskId(task.id)}
              >
                <span
                  className="m9-task-check"
                  onClick={(event) => {
                    event.stopPropagation()
                    toggleTask(task.id)
                  }}
                  title={task.status === 'blocked' ? 'Blocked task' : 'Toggle complete'}
                >
                  {task.status === 'complete' ? '✓' : ''}
                </span>
                <span className="m9-task-main">
                  <strong>{task.title}</strong>
                  <small>{CATEGORY_LABELS[task.category]} · Due {formatDueDate(task.dueDate)} · Score {task.priorityScore}/100</small>
                </span>
                <span className={`m9-priority ${task.priority}`}>{PRIORITY_LABELS[task.priority]}</span>
              </button>
            ))}
          </div>
          {filteredTasks.length > 5 && (
            <button className="m9-view-all" onClick={() => setShowAll((current) => !current)}>
              {showAll ? 'Show Less' : `View All Tasks (${filteredTasks.length})`}
            </button>
          )}
          <div className="m9-custom-inline">
            <div className="m9-custom-inline-title">
              <span>Custom Action</span>
              <strong>Add Task</strong>
            </div>
            <div className="m9-add-row">
              <input
                type="text"
                value={customTask}
                onChange={(event) => setCustomTask(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && addCustomTask()}
                placeholder="e.g. Ask broker for updated rent roll"
              />
              <button onClick={addCustomTask}>Add</button>
            </div>
          </div>
        </section>

        <section className="m9-detail-card">
          <div className="m9-title-row">
            <span>Action Detail</span>
            <strong>{activeTask ? statusLabel(activeTask.status) : 'None'}</strong>
          </div>
          {activeTask && (
            <>
              <div className="m9-detail-top">
                <div>
                  <span className={`m9-status-dot ${activeTask.status}`} />
                  <h2>{activeTask.title}</h2>
                </div>
                <span className={`m9-priority ${activeTask.priority}`}>{PRIORITY_LABELS[activeTask.priority]}</span>
              </div>
              <p>{activeTask.description}</p>
              <div className="m9-detail-grid">
                <div>
                  <span>Owner</span>
                  <strong>{activeTask.owner}</strong>
                </div>
                <div>
                  <span>Source</span>
                  <strong>{activeTask.source}</strong>
                </div>
                <div>
                  <span>Due</span>
                  <strong>{formatDueDate(activeTask.dueDate)}</strong>
                </div>
                <div>
                  <span>Status</span>
                  <strong>{statusLabel(activeTask.status)}</strong>
                </div>
                <div>
                  <span>Priority Score</span>
                  <strong>{activeTaskScore}/100</strong>
                </div>
                <div>
                  <span>AI Basis</span>
                  <strong>{activeTask.source}</strong>
                </div>
              </div>
              <div className="m9-impact-box">
                <span>Why it matters</span>
                <p>{activeTask.impact}</p>
              </div>
            </>
          )}
        </section>

        <section style={{
          background: '#FFFFFF',
          border: '1px solid #E2E8F0',
          borderRadius: 10,
          padding: 18,
          boxShadow: '0 8px 24px rgba(46,68,100,0.06)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 900, fontSize: 14, color: '#0F172A' }}>Deal Progress</span>
            <span style={{
              background: '#EFF6FF', color: '#1d4ed8', fontWeight: 900,
              fontSize: 13, padding: '5px 12px', borderRadius: 999,
              letterSpacing: 0.3, textTransform: 'uppercase',
            }}>
              {progress}% Done
            </span>
          </div>

          {/* SVG Progress Ring */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '8px 0' }}>
            {(() => {
              const size = 148
              const strokeW = 12
              const r = (size - strokeW) / 2
              const circ = 2 * Math.PI * r
              const offset = circ - (progress / 100) * circ
              return (
                <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
                  <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                    {/* Track */}
                    <circle cx={size/2} cy={size/2} r={r}
                      fill="none" stroke="#dbeafe" strokeWidth={strokeW} />
                    {/* Progress */}
                    <circle cx={size/2} cy={size/2} r={r}
                      fill="none" stroke="#1d4ed8" strokeWidth={strokeW}
                      strokeDasharray={circ}
                      strokeDashoffset={offset}
                      strokeLinecap="round"
                      style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                    />
                  </svg>
                  {/* Center text */}
                  <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    gap: 2,
                  }}>
                    <strong style={{ fontSize: 26, fontWeight: 900, color: '#0F172A', lineHeight: 1 }}>
                      {progress}%
                    </strong>
                    <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textAlign: 'center' }}>
                      {completeCount}/{tasks.length} done
                    </span>
                  </div>
                </div>
              )
            })()}
          </div>

          {/* Stats row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {[
              { label: 'Ready',   value: tasks.filter(t => t.status === 'ready').length,   color: '#16a34a', bg: '#f0fdf4' },
              { label: 'Pending', value: tasks.filter(t => t.status === 'pending').length, color: '#d97706', bg: '#fffbeb' },
              { label: 'Blocked', value: blockedCount,                                      color: '#dc2626', bg: '#fef2f2' },
            ].map(s => (
              <div key={s.label} style={{
                background: s.bg, borderRadius: 8,
                padding: '10px 8px', textAlign: 'center',
                border: `1px solid ${s.color}22`,
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: 0.5, marginBottom: 4, textTransform: 'uppercase' }}>
                  {s.label}
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, color: s.color, lineHeight: 1 }}>
                  {s.value}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="m9-cadence-card">
          <div className="m9-title-row">
            <span>Follow-up Cadence</span>
            <strong>Next 14 Days</strong>
          </div>
          <div className="m9-timeline">
            {[
              { day: 'Today', label: 'Call broker and confirm process' },
              { day: 'Day 2', label: 'Send LOI or final offer terms' },
              { day: 'Day 3', label: 'Follow up with lenders' },
              { day: 'Day 7', label: 'Request seller call and diligence files' },
              { day: 'Day 14', label: 'Escalate stale responses or pause deal' },
            ].map((item) => (
              <div key={item.day} className="m9-timeline-row">
                <span>{item.day}</span>
                <p>{item.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="m9-ai-card">
          <div className="m9-title-row">
            <span>Proactive AI Prompts</span>
            <strong>{proactivePrompts.length} available</strong>
          </div>
          <div className="m9-prompt-list">
            {proactivePrompts.map((prompt) => (
              <div key={prompt.id} className="m9-prompt-row">
                <div>
                  <strong>{prompt.title}</strong>
                  <p>{prompt.description}</p>
                  <span>{PRIORITY_LABELS[prompt.priority]} · Due {formatDueDate(prompt.dueDate)} · Score {scoreTask(prompt, dealState)}/100</span>
                </div>
                <button onClick={() => applyPrompt(prompt)}>Add Action</button>
              </div>
            ))}
            {!proactivePrompts.length && (
              <div className="m9-prompt-empty">
                AI has no new prompts right now. Complete or update tasks to surface the next recommended action.
              </div>
            )}
          </div>
        </section>

      </div>
      <div className="module-bottom-advance">
        <button className="m9-advance-btn" onClick={handleComplete}>
          Finalize Action Plan &amp; Advance to Module 10
        </button>
      </div>
    </div>
  )
}

export default Module9Page
